import { Button } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import type { MouseEvent } from 'react';
import vconst from 'utils/helpers/structs/vconst';
import SvgUser from 'components/svg/SvgUser';
import SvgCheckSquare from 'components/svg/SvgCheckSquare';
import EToolTip from 'components/others/EToolTip';
import type { LeadProposalGenerationType, LeadType } from '@econtractor/types';
import type { NewObjectType } from './types';
import { useAuth } from 'context/AuthContext';
import { useSettings } from 'context/SettingsContext';
import useSettingsParams from 'hooks/useSettingsParams';
import { hasUserAccess } from 'utils/helpers/access/hasUserAccess';
import { useNeedPay } from 'modules/admin';
import { buildNewObjectFromLead } from './helpers/proposalAmountToObjectPrice';
import SolarIcon from 'components/icons/SolarIcon';

interface LeedCardToolBarProps {
    setNewEvent: (event: any) => void;
    lead?: LeadType | null;
    setNewObject: (obj: NewObjectType) => void;
    setClientInfoId: (id: number | null) => void;
    onShowTasks: () => void;
    onRefresh: () => void | Promise<void>;
    acceptedGeneration?: LeadProposalGenerationType | null;
}

const LeedCardToolBar = ({
    setNewEvent,
    lead,
    setNewObject,
    setClientInfoId,
    onShowTasks,
    onRefresh,
    acceptedGeneration = null,
}: LeedCardToolBarProps) => {
    const { t } = useTranslation(['Leads', 'Clients', 'common']);
    const navigate = useNavigate();
    const { auth } = useAuth();
    const { localSettings } = useSettings();
    const { needPay } = useNeedPay();
    const params = useSettingsParams();
    const canCreateObject = hasUserAccess(auth?.user, 'objectCreate');
    const canShowTasks = params.tasksModule && params.tasksForLeads;
    const rate = Number(localSettings?.RATE?.current ?? 1) || 1;

    const handleClickStatus = (e: MouseEvent<HTMLButtonElement>, statusID: number) => {
        e.currentTarget.blur();
        if (!lead) return;
        if (needPay()) return;
        setNewEvent({
            leadId: lead.ID,
            statusID,
            leadPendingDt:
                statusID === vconst.leadPostponed ? Number(lead.leadPendingDt) || 0 : 0,
        });
    };

    return (
        <div className="lead-card-toolbar">
            <div className="lead-card-toolbar__actions">
                <Button
                    className="lead-card-toolbar__btn"
                    variant="success"
                    size="sm"
                    disabled={!lead}
                    onClick={(e) => handleClickStatus(e, lead?.leadStatus || 0)}
                >
                    <SolarIcon name="plus" />
                    {t('leadEvent.btnAddEvent')}
                </Button>

                <EToolTip
                    title={
                        lead?.leadStatus === vconst.leadAccept
                            ? t('toolGoToObjectHint')
                            : t('toolCreateObjectHint')
                    }
                >
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__btn toolbar-label-btn"
                            variant="success"
                            size="sm"
                            disabled={!lead || (lead.leadStatus !== vconst.leadAccept && !canCreateObject)}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead) return;
                                if (needPay()) return;
                                if (lead.leadStatus === vconst.leadAccept) navigate(`/objects/${lead.objectId}`);
                                else setNewObject(buildNewObjectFromLead(lead, acceptedGeneration, rate));
                            }}
                        >
                            <SolarIcon name="brightness-high" />
                            <span className="d-none d-md-inline ms-1">
                                {lead?.leadStatus === vconst.leadAccept
                                    ? t('toolGoToObject')
                                    : t('toolCreateObject')}
                            </span>
                        </Button>
                    </span>
                </EToolTip>

                {canShowTasks && (
                <EToolTip title={t('toolTask')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__btn toolbar-label-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead) return;
                                onShowTasks();
                            }}
                        >
                            <SvgCheckSquare width={14} height={14} />
                            <span className="d-none d-md-inline ms-1">{t('toolTask')}</span>
                        </Button>
                    </span>
                </EToolTip>
                )}

                <EToolTip title={t('common:toolRefreshList')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__icon-btn toolbar-icon-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                void onRefresh();
                            }}
                        >
                            <SolarIcon name="arrow-clockwise" />
                        </Button>
                    </span>
                </EToolTip>

                <EToolTip title={t('Clients:clientPopup.viewHint')}>
                    <span className="d-inline-block">
                        <Button
                            className="lead-card-toolbar__icon-btn toolbar-icon-btn"
                            size="sm"
                            disabled={!lead}
                            onClick={(e) => {
                                e.currentTarget.blur();
                                if (!lead) return;
                                setClientInfoId(lead?.clientId || null);
                            }}
                        >
                            <SvgUser width={14} height={14} />
                        </Button>
                    </span>
                </EToolTip>
            </div>
        </div>
    );
};

export default LeedCardToolBar;
